// client/components/ontology-v1/OntologyGraph.tsx
// Lightweight graph renderer for GraphBundle (edgesfix.txt implementation)
// Fixes: node sizing, breadthfirst layout, CSS-based missing edge toggle

import React, { useEffect, useRef } from 'react';
import cytoscape from 'cytoscape';

// Import GraphBundle type from backend
export interface GraphNode {
  id: string;
  label: string;
  kind: 'Class' | 'Individual' | 'Property';
  type?: 'Class' | 'Individual' | 'Property';  // Alias for kind
  pillar?: 'F' | 'A' | 'I' | 'R';
  parent?: string;
  [key: string]: unknown;  // Index signature for cytoscape element data compatibility
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  pillar: 'F' | 'A' | 'I' | 'R';
  status: 'satisfied' | 'missing';
  satisfiedBy?: string[];
  [key: string]: unknown;  // Index signature for cytoscape element data compatibility
}

export interface GraphBundle {
  nodes: GraphNode[];
  edges: GraphEdge[];
  stats: { totalEdges: number; satisfied: number };
  buildTimestamp?: string;
}

type Props = {
  data: GraphBundle;
  showMissing?: boolean;
  pillarFilter?: 'F' | 'A' | 'I' | 'R' | 'All';
};

const NODE_BASE = {
  'background-color': '#3b82f6',
  'label': 'data(label)',
  'text-valign': 'center',
  'text-halign': 'center',
  'color': '#fff',
  'font-size': 10,
  'width': 28,
  'height': 28,
  'shape': 'ellipse',
  'overlay-opacity': 0,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- cytoscape Stylesheet type was removed in newer versions
const STYLES: any[] = [
  {
    selector: 'node[kind = "Class"]',
    style: { ...NODE_BASE }
  },
  {
    selector: 'node[kind = "Individual"]',
    style: {
      ...NODE_BASE,
      'background-color': '#8b5cf6',
      'shape': 'round-rectangle',
      'width': 34,          // clamp the dataset id
      'height': 24,
      'text-wrap': 'ellipsis',
      'text-max-width': 120
    }
  },
  {
    selector: 'node[kind = "Property"]',
    style: {
      ...NODE_BASE,
      'background-color': '#10b981',
      'shape': 'diamond'
    }
  },
  {
    selector: 'edge[status = "satisfied"]',
    style: {
      'width': 2,
      'line-color': '#3b82f6',
      'target-arrow-color': '#3b82f6',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'label': 'data(label)',
      'font-size': 9,
      'text-rotation': 'autorotate',
      'text-margin-y': -6
    }
  },
  {
    selector: 'edge[status = "missing"]',
    style: {
      'width': 1,
      'line-style': 'dashed',
      'line-color': '#cbd5e1',
      'target-arrow-color': '#cbd5e1',
      'target-arrow-shape': 'triangle',
      'opacity': 0.7,
      'curve-style': 'bezier',
      'label': 'data(label)',
      'font-size': 8,
      'text-rotation': 'autorotate',
      'text-margin-y': -6
    }
  },
  // Toggle: hide missing edges when checkbox is off (we add/remove a class on the container)
  { selector: '.hide-missing edge[status = "missing"]', style: { 'display': 'none' } }
];

function makeLayout() {
  // breadthfirst gives a clean layered look; if you ship cose-bilkent, switch to that.
  return {
    name: 'breadthfirst' as const,
    directed: true,
    spacingFactor: 1.2,
    avoidOverlap: true,
    padding: 20,
    roots: `[id = "ex:Dataset"]`
  };
}

// Edge click → show which rules satisfied it
function wireEvents(cy: cytoscape.Core) {
  cy.on('tap', 'edge', (evt) => {
    const e = evt.target;
    const rules = (e.data('satisfiedBy') || []) as string[];
    const msg = rules.length
      ? `✓ ${e.data('label')}: satisfied by ${rules.join(', ')}`
      : `○ ${e.data('label')}: no evidence found`;
    console.log('[OntologyGraph]', msg);
    // TODO: your toast/status function here
    // showFairAnalysisStatus(rules.length ? 'success' : 'warn', msg);
  });
}

export function OntologyGraph({ data, showMissing = true, pillarFilter = 'All' }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const elements: Array<{ data: Record<string, unknown> }> = [];

    // Filter by pillar if needed
    const pillars = new Set<'F' | 'A' | 'I' | 'R'>(pillarFilter === 'All' ? ['F','A','I','R'] : [pillarFilter]);

    data.nodes.forEach(n => elements.push({ data: n }));
    data.edges
      .filter(e => pillars.has(e.pillar))
      .forEach(e => elements.push({ data: e }));

    const cy = cytoscape({
      container: containerRef.current!,
      elements,
      style: STYLES,
      wheelSensitivity: 0.2,
      pixelRatio: 1
    });

    cy.layout(makeLayout()).run();
    wireEvents(cy);

    const cls = 'hide-missing';
    if (!showMissing) cy.container()?.classList.add(cls);
    else cy.container()?.classList.remove(cls);

    return () => cy.destroy();
  }, [data, showMissing, pillarFilter]);

  return (
    <div className="relative">
      {/* Small HUD */}
      <div className="absolute left-3 top-3 bg-white/80 rounded-md px-3 py-2 text-xs shadow">
        <div>Nodes: {data.nodes.length}</div>
        <div>Edges: {data.stats.satisfied} / {data.stats.totalEdges}</div>
      </div>

      {/* Legend */}
      <div className="absolute right-3 top-3 bg-white/85 rounded-md px-3 py-2 text-xs shadow">
        <div className="font-semibold mb-1">Node Types</div>
        <div>● Class &nbsp;&nbsp; ■ Individual &nbsp;&nbsp; ◆ Property</div>
        <div className="font-semibold mt-2 mb-1">Edge Status</div>
        <div>— Satisfied &nbsp;&nbsp; - - - Missing</div>
      </div>

      <div ref={containerRef} style={{ width: '100%', height: 560 }} />
    </div>
  );
}
